import React, { useEffect, useState } from 'react';
import { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import { router } from '@forge/bridge';

import { OptionItemObject, SavingModel } from '../../../../../src/shared_models/interfaces';
import issueApi from '../../api/issueApi';
import serverApi from '../../api/serverApi';
import fieldForgeApi from '../../bridge/fieldForgeApi';
import { ErrorResponseObject } from '../../interfaces';
import { IS_NOT_EMPTY, ISSUES } from '../../utils/constants';
import { deepSearch, logResponseError } from '../../utils/levelModelUtils';
import { buildModel } from '../edit/EditMultilevelCustomField';
import { OptionTranslated } from '../edit/FieldSelect';

import { ABSOLUTE } from './constants';
import {
    CustomFieldValue,
    IssueItem,
    IssueNormalized,
    IssuesWithFieldValueSection as IssuesFoundProperties,
    OptionWithUpdatedName,
    UpdateOptionNameModalProperties as Properties,
} from './interfaces';
import { PositionedElement } from './styles';
import { ScrollableWrapper } from './UpdateOptionNameModalStyled';

const renderUpdatePairNames = (isSingleOptionUpdate: boolean, optionsWithUpdatedName: OptionWithUpdatedName[]) => {
    if (isSingleOptionUpdate) {
        const option = optionsWithUpdatedName[0];

        return (
            <strong>
                <span>
                    {option.previous.value} &gt; {option.current.value}
                </span>
            </strong>
        );
    }

    return (
        <strong>
            <br />
            {optionsWithUpdatedName.map(option => (
                <p key={option.previous.value}>
                    {option.previous.value} &gt; {option.current.value}
                </p>
            ))}
        </strong>
    );
};

const buildTranslatedOptions = (ids: string[], options: OptionItemObject[]): Map<string, OptionTranslated> => {
    const map = ids.reduce((acc: Map<string, OptionTranslated>, id: string) => {
        const option = deepSearch(options, id, { found: undefined });
        if (option) {
            acc.set(option.value, {
                value: option.key,
                label: option.value,
                translation: option.translation,
            });
        }
        return acc;
    }, new Map<string, OptionTranslated>());
    return map;
};

const renderBodyText = (isSingleOptionUpdate: boolean, optionsWithUpdatedName: OptionWithUpdatedName[]) => (
    <div>
        <p>
            Confirm that you want to update option{isSingleOptionUpdate ? '' : 's'} name{isSingleOptionUpdate ? '' : 's'}:
            &nbsp;{renderUpdatePairNames(isSingleOptionUpdate, optionsWithUpdatedName)}
            {isSingleOptionUpdate ? '.' : ''}
        </p>
        <p>
            Updating a custom field option <strong>replaces all matching values in all issues</strong>.
        </p>
    </div>
);

const getJqlTextForMatchingOptionsToUpdate = (optionsNames: OptionWithUpdatedName[], fieldName: string) => {
    const orStatements = optionsNames.map((option, index) =>
        option.parent ?
            ` ( "${fieldName}.ids" = "${option.parent.key}" AND "${fieldName}.ids" = "${option.previous.key}" )`
        :   ` "${fieldName}.ids" = "${option.previous.key}" `
    );

    return `"${fieldName}" ${IS_NOT_EMPTY} AND ( ` + orStatements.join(' OR ') + ' )';
};

const getIssueNavigatorUrl = (baseUrl: string, optionsNames: OptionWithUpdatedName[], fieldName: string) =>
    `${baseUrl}/${ISSUES}/?jql=${getJqlTextForMatchingOptionsToUpdate(optionsNames, fieldName)}`;

const IssuesWithFieldValueSection: React.FC<IssuesFoundProperties> = ({ issuesFound, url, isSingleOptionUpdate }) => (
    <p>
        Issues with updated name{isSingleOptionUpdate ? '' : 's'}: &nbsp;<strong>{issuesFound}</strong>
        &nbsp;
        {issuesFound > 0 && (
            <>
                &#40;<a onClick={() => router.open(url)}>find them in the Issue Navigator</a>&#41;
            </>
        )}
    </p>
);

const normalizeIssues = (issues: IssueItem[], customField: string): IssueNormalized[] =>
    issues.map((issue: IssueItem) => ({
        id: issue.id,
        key: issue.key,
        customFieldValue: issue.fields[customField],
    }));

const updateOptionsInField = (value: CustomFieldValue, options: OptionItemObject[]): SavingModel => {
    const translatedCurrentOptions = buildTranslatedOptions(value.ids, options);
    return buildModel(translatedCurrentOptions, undefined)!;
};

const updateOptionsInIssue = async (issue: IssueNormalized, customField: string, options: OptionItemObject[]) => {
    const { customFieldValue } = issue;
    const newcustomFieldValue = updateOptionsInField(customFieldValue, options);
    const body = {
        fields: {
            [customField]: newcustomFieldValue,
        },
    };
    await issueApi.updateIssue(JSON.stringify(body), issue.key);
};

const UpdateOptionNameModal: React.FC<Properties> = ({ customField, onClose, onSubmit, options, optionsWithUpdatedName }) => {
    const [isFetching, setIsFetching] = useState(true);
    const [issuesWithMatchingOptionName, setIssuesWithMatchingOptionName] = useState<IssueNormalized[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [updatedIssueCounter, setUpdatedIssueCounter] = useState(0);
    const [baseUrl, setBaseUrl] = useState('');
    const [fieldName, setFieldName] = useState('');

    const isSingleOptionUpdate = optionsWithUpdatedName.length === 1;

    const fetchData = async () => {
        try {
            const baseUrl = await serverApi.getBaseUrl();
            const fields = await fieldForgeApi.getFields();
            const field = fields.find((fieldValue: any) => fieldValue.id === customField);
            const jqlToSearch = getJqlTextForMatchingOptionsToUpdate(optionsWithUpdatedName, field?.name as string);
            const issues = (await issueApi.getAllIssuesWithJql(jqlToSearch))?.issues || [];

            setBaseUrl(baseUrl);
            setFieldName(field?.name as string);
            setIssuesWithMatchingOptionName(normalizeIssues(issues, customField));
        } catch (error) {
            logResponseError(error as ErrorResponseObject);
        } finally {
            setIsFetching(false);
        }
    };

    const handleUpdate = async () => {
        setIsSaving(true);
        for (const issue of issuesWithMatchingOptionName) {
            await updateOptionsInIssue(issue, customField, options);
            setUpdatedIssueCounter(prevState => prevState + 1);
        }
        await onSubmit();
        onClose();
        setIsSaving(false);
    };

    const handleModalClosing = () => {
        onClose();
    };

    useEffect(() => {
        void fetchData();
    }, []);

    return (
        <Modal
            onClose={() => !isSaving && handleModalClosing()}
            autoFocus={false}
            height={800}
        >
            <ModalHeader>
                <ModalTitle appearance='danger'>Update option{isSingleOptionUpdate ? '' : 's'}</ModalTitle>
            </ModalHeader>
            <ScrollableWrapper>
                <ModalBody>
                    {renderBodyText(isSingleOptionUpdate, optionsWithUpdatedName)}
                    {isFetching ?
                        <PositionedElement position={ABSOLUTE}>
                            <Spinner size='medium' />
                        </PositionedElement>
                    :   <IssuesWithFieldValueSection
                            issuesFound={issuesWithMatchingOptionName.length}
                            url={getIssueNavigatorUrl(baseUrl, optionsWithUpdatedName, fieldName)}
                            isSingleOptionUpdate={isSingleOptionUpdate}
                        />
                    }
                    {isSaving && (
                        <p>
                            Issues updated so far: <strong>{updatedIssueCounter}</strong>
                        </p>
                    )}
                </ModalBody>
            </ScrollableWrapper>
            <ModalFooter>
                <ButtonGroup>
                    <LoadingButton
                        onClick={handleModalClosing}
                        appearance='subtle'
                        isLoading={isSaving}
                    >
                        Cancel
                    </LoadingButton>
                    <LoadingButton
                        appearance='danger'
                        onClick={handleUpdate}
                        isLoading={isFetching || isSaving}
                    >
                        Update
                    </LoadingButton>
                </ButtonGroup>
            </ModalFooter>
        </Modal>
    );
};

export default UpdateOptionNameModal;
